/**
 * 4Boats AI Search — powered by Claude (Anthropic)
 *
 * Setup: pune API key-ul tau Anthropic mai jos.
 * Il gasesti la: https://console.anthropic.com → API Keys
 */
const ANTHROPIC_API_KEY = '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';

(function () {
  'use strict';

  const SLOW_SEARCH_DELAY = 5000;
  const DEBOUNCE_DELAY = 400;
  const SHOP_DOMAIN = window.Shopify?.shop || '4boat-co-uk.myshopify.com';

  // ─── Main controller ─────────────────────────────────────────────────────────

  class AISearchController {
    constructor(predictiveEl) {
      this.root = predictiveEl;
      this.input = predictiveEl.querySelector('input[type="search"]');
      this.resultsEl = predictiveEl.querySelector('#predictive-search');

      if (!this.input || !this.resultsEl) return;

      this._debounce = null;
      this._slowTimer = null;
      this._currentQuery = '';
      this._abortCtrl = null;

      this._attachEvents();
    }

    _attachEvents() {
      this.input.addEventListener('input', () => this._onInput(), true);
      document.addEventListener('keydown', e => { if (e.key === 'Escape') this._hide(); });
      document.addEventListener('click', e => { if (!this.root.contains(e.target)) this._hide(); });
    }

    _onInput() {
      const query = this.input.value.trim();

      clearTimeout(this._debounce);
      clearTimeout(this._slowTimer);

      if (query.length < 2) { this._hide(); return; }

      this._currentQuery = query;
      this._showLoading(query);

      this._debounce = setTimeout(() => this._runSearch(query), DEBOUNCE_DELAY);
    }

    async _runSearch(query) {
      if (this._abortCtrl) this._abortCtrl.abort();
      this._abortCtrl = new AbortController();

      this._slowTimer = setTimeout(() => {
        if (this._currentQuery === query) this._showSlowMessage();
      }, SLOW_SEARCH_DELAY);

      try {
        // Part numbers — skip AI, search directly
        const isPartNumber = /^[a-z0-9]{2,}-[a-z0-9][\w-]*$/i.test(query.trim());

        let searchTerms = [query];
        let message = '';

        if (isPartNumber) {
          const noHyphens = query.replace(/-/g, '');
          if (noHyphens !== query) searchTerms = [query, noHyphens];
        } else {
          // AI step — mandatory. If Claude fails, show error not silent fallback.
          try {
            const ai = await callClaude(query, this._abortCtrl.signal);
            searchTerms = ai.searchTerms.length ? ai.searchTerms : [query];
            message = ai.message;
          } catch (claudeErr) {
            if (claudeErr.name === 'AbortError') return;
            // AI failed — fall back to original query but log clearly
            console.warn('[4Boats AI] Claude unavailable:', claudeErr.message);
            searchTerms = [query];
          }
        }

        if (this._currentQuery !== query) return;

        // Pass 1 — cauta dupa TITLU si tip produs (rezultate cele mai relevante)
        const seen = new Map();
        for (const term of searchTerms) {
          const products = await searchShopify(term, 'title,product_type,variants.title');
          products.forEach(p => { if (!seen.has(p.url)) seen.set(p.url, p); });
          if (this._currentQuery !== query) return;
        }

        // Pass 2 — cauta dupa DESCRIERE pentru rezultate suplimentare
        for (const term of searchTerms) {
          const products = await searchShopify(term, 'body');
          products.forEach(p => { if (!seen.has(p.url)) seen.set(p.url, p); });
          if (this._currentQuery !== query) return;
        }

        // Fallback — daca AI a corectat dar nu s-a gasit nimic, incearca cuvinte individuale dupa titlu
        if (seen.size === 0) {
          const words = query.trim().split(/\s+/).filter(w => w.length >= 4);
          for (const word of words) {
            const products = await searchShopify(word, 'title,product_type');
            products.forEach(p => { if (!seen.has(p.url)) seen.set(p.url, p); });
            if (this._currentQuery !== query) return;
          }
        }

        clearTimeout(this._slowTimer);
        if (this._currentQuery !== query) return;

        this._showResults({ products: [...seen.values()].slice(0, 8), message }, query);

      } catch (err) {
        clearTimeout(this._slowTimer);
        if (err.name === 'AbortError') return;
        console.error('[4Boats AI Search]', err);
        if (this._currentQuery === query) this._showError(query);
      }
    }

    // ─── Render ──────────────────────────────────────────────────────────────

    _showLoading(query) {
      this._setResults(`
        <div class="ai-search-state ai-search-loading">
          <span class="ai-search-spinner"></span>
          <span>AI is searching for <em>${esc(query)}</em>&hellip;</span>
        </div>
      `);
    }

    _showSlowMessage() {
      const span = this.resultsEl.querySelector('.ai-search-loading span:last-child');
      if (span) span.textContent = 'Searching through all products on the site to find the best match for you…';
    }

    _showResults(data, query) {
      const { products = [], message } = data;

      if (products.length === 0) {
        this._setResults(`
          <div class="ai-search-state ai-search-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <p>No products found for <strong>${esc(query)}</strong>.</p>
            <button class="ai-search-noresults-btn" data-query="${esc(query)}">
              Can&rsquo;t find it? Tell us what you need &rarr;
            </button>
            <a href="/collections/all" class="ai-search-browse">Browse all categories</a>
          </div>
        `);
        // Attach click after render — stopPropagation previne inchiderea accidentala
        const btn = this.resultsEl.querySelector('.ai-search-noresults-btn');
        if (btn) btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          const q = query;
          this._hide();
          // Delay so the button click fully settles before the modal overlay appears
          setTimeout(() => showNoResultsModal(q), 80);
        });
        return;
      }

      const msgHtml = message ? `
        <div class="ai-search-message" role="status">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
          </svg>
          <span>${esc(message)}</span>
        </div>` : '';

      const productsHtml = products.map(p => `
        <a href="${esc(p.url)}" class="ai-search-product" tabindex="0">
          <div class="ai-search-product__img">
            ${p.image
              ? `<img src="${esc(p.image)}" alt="${esc(p.title)}" loading="lazy" width="56" height="56">`
              : '<div class="ai-search-product__img-placeholder"></div>'}
          </div>
          <div class="ai-search-product__info">
            <span class="ai-search-product__title">${esc(p.title)}</span>
            ${p.price ? `<span class="ai-search-product__price">${esc(p.price)}</span>` : ''}
            ${!p.available ? '<span class="ai-search-product__oos">Out of stock</span>' : ''}
          </div>
          <svg class="ai-search-product__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
        </a>
      `).join('');

      this._setResults(`
        ${msgHtml}
        <div class="ai-search-products" role="listbox">${productsHtml}</div>
        <a href="/search?q=${encodeURIComponent(query)}&type=product" class="ai-search-view-all">
          View all results for &ldquo;${esc(query)}&rdquo;
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </a>
      `);
    }

    _showError(query) {
      this._setResults(`
        <div class="ai-search-state ai-search-error">
          <p>Search is temporarily unavailable.</p>
          <a href="/search?q=${encodeURIComponent(query)}&type=product">Try standard search &rarr;</a>
        </div>
      `);
    }

    _setResults(html) {
      this.resultsEl.innerHTML = html;
      this.resultsEl.removeAttribute('hidden');
      this.resultsEl.style.display = 'block';
      this.root.classList.add('ai-search-active');
    }

    _hide() {
      clearTimeout(this._slowTimer);
      this.resultsEl.style.display = 'none';
      this.root.classList.remove('ai-search-active');
    }
  }

  // ─── Claude API (direct) ──────────────────────────────────────────────────────

  async function callClaude(query, signal) {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': ANTHROPIC_API_KEY,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      signal,
      body: JSON.stringify({
        model: 'claude-3-5-haiku-20241022',
        max_tokens: 200,
        system: `You are a product search assistant for 4Boats.co.uk, a UK marine & boating equipment store.
Categories: Safety & rescue, Marine batteries, Anchors, Boat electrical & lighting, Fishing gear, Chandlery, Kayaks & SUP, Bilge pumps, Bimini tops, Boat hardware, Outboard parts (Yamaha Honda Suzuki Johnson), Electric outboards, Solar power, Boat seats, Stainless steel fittings.
Respond with valid JSON only — no markdown.`,
        messages: [{
          role: 'user',
          content: `Customer searched: "${query}"
Return JSON: {"searchTerms":["term1","term2"],"message":"one friendly sentence","intent":"brief"}
Rules for searchTerms:
- FIRST correct any typos/misspellings (e.g. "sterter"→"starter", "outbord"→"outboard", "propeler"→"propeller", "engin"→"engine", "impeler"→"impeller").
- For multi-word queries, break into 2-word combinations that match product titles: "starter honda 40hp" → ["honda starter", "starter motor"]. Never return a single brand name alone (e.g. never just "honda" or "yamaha" — too generic).
- If the query looks like a part number or SKU (e.g. "6h4-81800-12"), keep it exactly as-is as the first term.
- Return 2 search terms maximum. Each must be a 1-2 word phrase specific enough to match product titles (not just a brand name).
- In the message field, if you corrected a typo say: "Showing results for [corrected term]…"`,
        }],
      }),
    });

    if (!res.ok) throw new Error(`Claude ${res.status}`);

    const data = await res.json();
    const raw = data.content?.[0]?.text || '{}';
    const clean = raw.replace(/```json?\n?/g, '').replace(/```/g, '').trim();

    try {
      const parsed = JSON.parse(clean);
      return {
        searchTerms: Array.isArray(parsed.searchTerms) && parsed.searchTerms.length
          ? parsed.searchTerms.slice(0, 3)
          : [query],
        message: parsed.message || '',
      };
    } catch {
      return { searchTerms: [query], message: '' };
    }
  }

  // ─── Shopify Search ───────────────────────────────────────────────────────────

  async function searchShopify(term, fields = 'title,product_type,variants.title') {
    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product',
      'resources[limit]': '6',
      'resources[options][fields]': fields,
    });

    // URL relativ — evita CORS intre 4boats.co.uk si myshopify.com
    const res = await fetch(`/search/suggest.json?${params}`, {
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) return [];

    const data = await res.json();
    return (data.resources?.results?.products || []).map(p => ({
      title: p.title || '',
      url: p.url || '',
      image: p.image || null,
      price: p.price ? '£' + parseFloat(p.price).toFixed(2) : null,
      available: p.available !== false,
    }));
  }

  // ─── No-Results Modal ─────────────────────────────────────────────────────────

  let _modal = null;
  let _lastQuery = '';

  function showNoResultsModal(query) {
    _lastQuery = query || '';
    if (!_modal) _modal = buildModal();

    // Pre-fill textarea placeholder with query
    const ta = _modal.querySelector('.ai-modal__message');
    if (ta) ta.placeholder = `I was searching for: "${_lastQuery}"\n\nExtra details (model, year, part number…)`;

    // Pre-fill WhatsApp link
    const waBtn = _modal.querySelector('.ai-modal__wa');
    if (waBtn) waBtn.href = `https://wa.me/447500282085?text=${encodeURIComponent('Hi, I\'m looking for: ' + _lastQuery)}`;

    // Reset form
    const form = _modal.querySelector('.ai-modal__form');
    form.reset();
    _modal.querySelector('.ai-modal__success').hidden = true;
    _modal.querySelector('.ai-modal__error').hidden = true;
    const submitBtn = form.querySelector('.ai-modal__submit');
    submitBtn.disabled = false;
    submitBtn.textContent = 'Send Request';

    _modal.removeAttribute('hidden');
    requestAnimationFrame(() => _modal.classList.add('ai-modal--open'));
    document.body.style.overflow = 'hidden';
  }

  function hideModal() {
    if (!_modal) return;
    _modal.classList.remove('ai-modal--open');
    document.body.style.overflow = '';
    setTimeout(() => _modal && _modal.setAttribute('hidden', ''), 280);
  }

  function buildModal() {
    const el = document.createElement('div');
    el.id = 'ai-search-modal';
    el.className = 'ai-modal';
    el.setAttribute('hidden', '');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Product request form');

    el.innerHTML = `
      <div class="ai-modal__overlay"></div>
      <div class="ai-modal__box">
        <button class="ai-modal__close" aria-label="Close">&times;</button>

        <div class="ai-modal__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
        </div>

        <h2 class="ai-modal__title">Can&rsquo;t find what you&rsquo;re looking for?</h2>
        <p class="ai-modal__subtitle">We source thousands of marine parts. Tell us what you need and we&rsquo;ll track it down for you!</p>

        <form class="ai-modal__form">
          <div class="ai-modal__row">
            <input class="ai-modal__input" type="text" name="customer_name" placeholder="Your name *" required autocomplete="name">
            <input class="ai-modal__input" type="email" name="customer_email" placeholder="Your email *" required autocomplete="email">
          </div>
          <textarea class="ai-modal__input ai-modal__message" name="customer_message" rows="3" placeholder="Describe the product you need…"></textarea>
          <button type="submit" class="ai-modal__submit">Send Request</button>
          <p class="ai-modal__success" hidden>&#10003; Message sent! We&rsquo;ll be in touch soon.</p>
          <p class="ai-modal__error" hidden>Something went wrong. Please try again or contact us directly.</p>
        </form>

        <div class="ai-modal__divider"><span>or chat with us directly</span></div>

        <div class="ai-modal__contacts">
          <a href="https://wa.me/447500282085" target="_blank" rel="noopener" class="ai-modal__contact-btn ai-modal__wa ai-modal__wa--full">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.118 1.524 5.845L0 24l6.332-1.499A11.933 11.933 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.808 9.808 0 01-5.002-1.368l-.36-.214-3.757.889.943-3.658-.235-.374A9.786 9.786 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z"/></svg>
            Chat on WhatsApp — 07500 282085
          </a>
        </div>

        <div class="ai-modal__browse">
          <p>Browse popular categories:</p>
          <div class="ai-modal__cats">
            <a href="/collections/yamaha">Yamaha Parts</a>
            <a href="/collections/honda">Honda Parts</a>
            <a href="/collections/suzuki-outboard-parts">Suzuki Parts</a>
            <a href="/collections/rescue-and-safety-equipment">Safety</a>
            <a href="/collections/chandlery">Chandlery</a>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(el);

    // Delay overlay listener so the opening click cannot accidentally close the modal
    setTimeout(() => {
      el.querySelector('.ai-modal__overlay').addEventListener('click', hideModal);
    }, 400);
    el.querySelector('.ai-modal__close').addEventListener('click', hideModal);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') hideModal(); });
    el.querySelector('.ai-modal__form').addEventListener('submit', handleModalSubmit);

    return el;
  }

  async function handleModalSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const submitBtn = form.querySelector('.ai-modal__submit');
    const successEl = _modal.querySelector('.ai-modal__success');
    const errorEl = _modal.querySelector('.ai-modal__error');

    const name    = form.querySelector('[name="customer_name"]').value.trim();
    const email   = form.querySelector('[name="customer_email"]').value.trim();
    const message = form.querySelector('[name="customer_message"]').value.trim();

    if (!name || !email) return;

    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending…';
    errorEl.hidden = true;

    try {
      const bodyText =
        `Product Not Found — AI Search Request\n\n` +
        `Customer searched for: "${_lastQuery}"\n\n` +
        (message ? `Additional details:\n${message}\n\n` : '') +
        `Customer name: ${name}\nCustomer email: ${email}`;

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: '8f1d6964-53da-43b4-bc56-ce6c3a7247c8',
          subject: `4Boats Product Request: "${_lastQuery}"`,
          name,
          email,
          message: bodyText,
          botcheck: '',
        }),
      });

      const data = await res.json();

      if (data.success) {
        successEl.hidden = false;
        submitBtn.hidden = true;
        setTimeout(hideModal, 3500);
      } else {
        throw new Error(data.message || 'Failed');
      }
    } catch (err) {
      console.error('[4Boats Modal] Error:', err);
      errorEl.textContent = `Something went wrong (${err.message}). Please contact us directly at office@4boats.co.uk`;
      errorEl.hidden = false;
      submitBtn.disabled = false;
      submitBtn.textContent = 'Send Request';
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  function esc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function init() {
    document.querySelectorAll('predictive-search').forEach(el => {
      if (!el._aiSearch) el._aiSearch = new AISearchController(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('predictive-search:open', () => setTimeout(init, 50));

})();
